import React from 'react';

/**
 * The last screen before a white one.
 *
 * Rendered by AppErrorBoundary when the React tree throws, and directly by
 * index.js when `bootstrap()` rejects — a boundary cannot help there, because
 * the failure happens BEFORE `root.render` is ever called and there is no
 * tree to catch anything in. Both paths need the same screen, so it lives
 * here rather than inside the boundary.
 *
 * DEPENDENCIES ARE DELIBERATELY ABSENT. No i18n, no Modal, no store, no
 * lucide icon, no CSS class from index.css. Every one of those is a module
 * that could itself be the thing that just failed (i18n is initialised at the
 * top of index.js; preferences failing is literally the bootstrap case), and
 * a crash screen that crashes leaves the user with the white page this exists
 * to prevent. Inline styles only, plain React, English only — a fallback that
 * always renders beats a prettier one that sometimes does.
 *
 * Why no "send a report" button: reportService requires an authenticated
 * apiClient session (POST /reports, and its attachments endpoint refuses any
 * url not under its own storage), and ReportIssueModal is a form the user
 * fills in — topic, subject, rich-text body — after fetching /reports/meta.
 * Neither is callable from a crash handler: the crash may well be the auth
 * store, the network, or the render tree the modal would mount into. So the
 * offer here is "copy the details", which needs nothing but the clipboard,
 * and the user can paste them into Settings → About → Report an issue once
 * the app is running again.
 */

/**
 * The app's scrollbar look, without the app's scrollbar.
 *
 * The details box had the OS's fat native bar, which reads as "not part of this
 * program" in a dialog that is already alarming (owner, 23.09.2026). Everywhere
 * else that is `ui/OsScroll` — and OsScroll is OverlayScrollbars, a third-party
 * module that measures the document at init. Importing it here would break the
 * rule above for the one screen that exists BECAUSE a module failed, and its
 * failure mode is the white page.
 *
 * So: the same thin bar, written as a `<style>` element this component owns. No
 * import, no class from index.css, nothing that can be missing. The known cost
 * is the documented one — macOS WKWebView reverts a styled `::-webkit-scrollbar`
 * to the native bar once the pointer touches it, which is why OsScroll exists at
 * all. On a crash screen, a bar that looks right until you grab it is a trade
 * worth making; a crash screen that does not render is not.
 */
const SCROLLBAR_CSS = `
[data-testid="crash-fallback-detail"] { scrollbar-width: thin; }
[data-testid="crash-fallback-detail"]::-webkit-scrollbar { width: 10px; height: 10px; }
[data-testid="crash-fallback-detail"]::-webkit-scrollbar-track { background: transparent; }
[data-testid="crash-fallback-detail"]::-webkit-scrollbar-thumb {
  background-color: rgba(136, 105, 235, 0.35);
  border-radius: 8px;
  border: 3px solid transparent;
  background-clip: content-box;
}
[data-testid="crash-fallback-detail"]::-webkit-scrollbar-thumb:hover {
  background-color: rgba(136, 105, 235, 0.55);
}
[data-testid="crash-fallback-detail"]::-webkit-scrollbar-corner { background: transparent; }
`;

function isDarkTheme() {
  try {
    return document.documentElement.classList.contains('dark');
  } catch {
    return false;
  }
}

export default function CrashFallback({
  title = 'Something went wrong',
  message = 'Apiboo hit an unexpected error and could not continue.',
  detail = '',
  onRetry,
}) {
  const dark = isDarkTheme();
  const [copied, setCopied] = React.useState(false);

  const palette = dark
    ? { bg: '#0f0f12', panel: '#18181b', text: '#f4f4f5', muted: '#a1a1aa', border: '#2a2a30' }
    : { bg: '#f8fafc', panel: '#ffffff', text: '#18181b', muted: '#6b7280', border: '#e5e7eb' };

  const handleRetry = () => {
    if (typeof onRetry === 'function') {
      onRetry();
      return;
    }
    // Default retry is a full reload rather than a re-render: whatever state
    // produced the crash is still in memory, and re-rendering the same tree
    // over it usually just crashes again immediately.
    window.location.reload();
  };

  const handleCopy = () => {
    const payload = detail ? `${title}\n\n${detail}` : title;
    try {
      navigator.clipboard?.writeText(payload);
      setCopied(true);
    } catch {
      // Clipboard unavailable (permissions, no secure context). The details
      // are on screen and selectable — nothing else to do.
    }
  };

  return (
    <div
      role="alert"
      data-testid="crash-fallback"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: palette.bg,
        color: palette.text,
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontSize: '0.875rem',
        // The Tauri title bar is a React component too — if the tree crashed,
        // it is gone, so this must sit above everything and be draggable-free.
        zIndex: 2147483647,
        overflow: 'auto',
      }}
    >
      {/* Scoped to the details box by its own test id, so nothing else on the
          page is restyled by a crash screen. */}
      <style>{SCROLLBAR_CSS}</style>
      <div
        style={{
          maxWidth: '34rem',
          width: '100%',
          background: palette.panel,
          border: `1px solid ${palette.border}`,
          borderRadius: '0.75rem',
          padding: '1.5rem',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{title}</h1>
        <p style={{ margin: '0.75rem 0 0', color: palette.muted, lineHeight: 1.5 }}>{message}</p>

        {detail ? (
          <pre
            data-testid="crash-fallback-detail"
            style={{
              margin: '1rem 0 0',
              padding: '0.75rem',
              background: dark ? '#111114' : '#f3f4f6',
              border: `1px solid ${palette.border}`,
              borderRadius: '0.5rem',
              color: palette.muted,
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '12rem',
              overflow: 'auto',
            }}
          >
            {detail}
          </pre>
        ) : null}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleRetry}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              background: '#8869eb',
              color: '#ffffff',
            }}
          >
            Try again
          </button>
          {detail ? (
            <button
              type="button"
              onClick={handleCopy}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: `1px solid ${palette.border}`,
                cursor: 'pointer',
                background: 'transparent',
                color: palette.text,
              }}
            >
              {copied ? 'Copied' : 'Copy details'}
            </button>
          ) : null}
        </div>

        <p style={{ margin: '1rem 0 0', color: palette.muted, fontSize: '0.75rem' }}>
          Your data is stored locally and on the server — nothing has been lost. If this keeps
          happening, report it from Settings → About once the app is running again.
        </p>
      </div>
    </div>
  );
}
